import React, { useState, useEffect } from 'react';

const validateABNChecksum = (abn) => {
 const clean = abn.replace(/\s/g, '');
 if (!/^\d{11}$/.test(clean)) return { valid: false, reason: 'ABN must be 11 digits' };
 const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
 const digits = clean.split('').map(Number);
 digits[0] -= 1;
 const sum = digits.reduce((acc, d, i) => acc + d * weights[i], 0);
 return { valid: sum % 89 === 0, abn: clean };
};

// formatABN removed — unused

export default function ABNVerificationPage({ user }) {
 const [abn, setAbn] = useState('');
 const [businessName, setBusinessName] = useState('');
 const [abnValidation, setAbnValidation] = useState(null);
 const [currentStatus, setCurrentStatus] = useState(null);
 const [loading, setLoading] = useState(true);
 const [submitting, setSubmitting] = useState(false);
 const [success, setSuccess] = useState(null);
 const [error, setError] = useState('');
 // eslint-disable-next-line
  const [step, setStep] = useState(1);

 const token = localStorage.getItem('token');

 useEffect(() => {
 fetchABNStatus();
 }, []);

 const fetchABNStatus = async () => {
 try {
 const res = await fetch('/api/employer/abn', { headers: { Authorization: `Bearer ${token}` } });
 const data = await res.json();
 if (data.abn) {
 setCurrentStatus(data);
 if (data.verified) {
 setAbn(data.abn);
 setBusinessName(data.business_name || '');
 }
 }
 } catch (e) { console.error(e); }
 setLoading(false);
 };

 const handleABNChange = (e) => {
 const raw = e.target.value.replace(/[^\d\s]/g, '');
 const digits = raw.replace(/\s/g, '');
 if (digits.length > 11) return;
 setAbn(raw);
 if (digits.length === 11) {
 const result = validateABNChecksum(digits);
 setAbnValidation(result);
 } else {
 setAbnValidation(null);
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 setError('');
 setSubmitting(true);
 try {
 const res = await fetch('/api/employer/verify-abn', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
 body: JSON.stringify({ abn: abn.replace(/\s/g, ''), business_name: businessName })
 });
 const data = await res.json();
 if (res.ok) {
 setSuccess(data);
 setCurrentStatus({ abn: data.abn, verified: true, business_name: businessName, status: 'Active' });
 } else {
 setError(data.error || 'Verification failed');
 }
 } catch (e) {
 setError('Network error. Please try again.');
 }
 setSubmitting(false);
 };

 const digitsEntered = abn.replace(/\s/g, '').length;

 if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div></div>;

 return (
 <div className="min-h-screen bg-neutral-50 py-8 px-4">
 <div className="max-w-lg mx-auto">
 <div className="mb-6">
 <h1 className="text-2xl font-bold text-neutral-900">ABN Verification</h1>
 <p className="text-neutral-500 mt-1">Verify your Australian Business Number to build trust with workers.</p>
 </div>

 {/* Trust Badge Info */}
 <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-6">
 <h3 className="font-semibold text-brand-900 mb-1"> Why verify your ABN?</h3>
 <ul className="text-sm text-brand-800 space-y-1">
 <li>✓ Verified businesses get a trust badge visible to all workers</li>
 <li>✓ Workers are <strong>3x more likely</strong> to apply to verified business shifts</li>
 <li>✓ Builds credibility and reduces friction in hiring</li>
 <li>✓ Required for payment processing via Stripe</li>
 </ul>
 </div>

 {/* Already Verified */}
 {currentStatus?.verified && !success ? (
 <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
 <div className="flex items-center gap-3 mb-4">
 <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
 <span className="text-brand-600 text-xl">✓</span>
 </div>
 <div>
 <p className="font-semibold text-neutral-900">ABN Verified</p>
 <p className="text-sm text-neutral-500">{currentStatus.business_name}</p>
 </div>
 </div>
 <div className="bg-neutral-50 rounded-lg p-3 mb-4">
 <p className="text-xs text-neutral-500 mb-1">Registered ABN</p>
 <p className="font-mono text-lg font-semibold text-neutral-900">
 {currentStatus.abn?.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4')}
 </p>
 <p className="text-xs text-brand-600 mt-1">Status: {currentStatus.status}</p>
 </div>
 <button
 onClick={() => setCurrentStatus(prev => ({ ...prev, verified: false }))}
 className="text-sm text-blue-600 hover:underline"
 >
 Update ABN details →
 </button>
 </div>
 ) : success ? (
 <div className="bg-white rounded-xl shadow-sm border border-brand-300 p-6 text-center">
 <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
 <span className="text-brand-600 text-3xl">✓</span>
 </div>
 <h2 className="text-xl font-bold text-neutral-900 mb-1">ABN Verified!</h2>
 <p className="text-neutral-500 mb-4">{success.note}</p>
 <div className="bg-neutral-50 rounded-lg p-3 text-left">
 <p className="text-xs text-neutral-500 mb-1">Verified ABN</p>
 <p className="font-mono text-lg font-semibold text-neutral-900">
 {success.abn?.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4')}
 </p>
 {businessName && <p className="text-sm text-neutral-700 mt-1">{businessName}</p>}
 </div>
 <p className="text-sm text-brand-600 mt-4 font-medium"> Trust badge is now active on your profile</p>
 </div>
 ) : (
 <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
 <form onSubmit={handleSubmit} className="space-y-5">
 {/* ABN Input */}
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1">Australian Business Number (ABN)</label>
 <div className="relative">
 <input
 type="text"
 value={abn}
 onChange={handleABNChange}
 placeholder="51 824 753 556"
 className={`w-full border rounded-lg px-3 py-2.5 font-mono text-lg focus:outline-none focus:ring-2 pr-10 ${
 abnValidation?.valid === true ? 'border-brand-400 focus:ring-brand-500' :
 abnValidation?.valid === false ? 'border-red-400 focus:ring-red-500' :
 'border-neutral-200 focus:ring-brand-500'
 }`}
 />
 {digitsEntered === 11 && (
 <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xl ${abnValidation?.valid ? 'text-brand-500' : 'text-red-500'}`}>
 {abnValidation?.valid ? '✓' : '✗'}
 </span>
 )}
 </div>
 <div className="flex justify-between mt-1">
 {abnValidation?.valid === false && (
 <p className="text-xs text-red-600">Invalid ABN — checksum failed</p>
 )}
 {abnValidation?.valid === true && (
 <p className="text-xs text-brand-600">Valid ABN format ✓</p>
 )}
 <p className="text-xs text-neutral-400 ml-auto">{digitsEntered}/11 digits</p>
 </div>
 </div>

 {/* Business Name */}
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1">Legal Business Name</label>
 <input
 type="text"
 value={businessName}
 onChange={e => setBusinessName(e.target.value)}
 placeholder="e.g. Acme Hospitality Pty Ltd"
 className="w-full border border-neutral-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-500"
 />
 </div>

 {error && (
 <div className="bg-red-50 border border-red-200 rounded-lg p-3">
 <p className="text-sm text-red-700">{error}</p>
 </div>
 )}

 <button
 type="submit"
 disabled={submitting || !abnValidation?.valid}
 className="w-full bg-brand-600 hover:bg-brand-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
 >
 {submitting ? 'Verifying...' : 'Verify ABN'}
 </button>
 </form>
 </div>
 )}
 </div>
 </div>
 );
}
